import { useMemo, useState } from 'react';
import { sortBy } from 'lodash';
import { GridColDef } from '@mui/x-data-grid-pro';
import useSWR from 'swr';
import { PhoenixGrid } from '@shared/components/grid/PhoenixGrid';
import { PageWrapper } from '@shared/styledComponents';
import { dataGridCol } from '@shared/components/grid/common';
import { mapGridIndexes } from '@shared/commonFunctions';
import { GridColSelect } from '@shared/components/grid/GridColSelect';
import { GridColTextInput } from '@shared/components/grid/GridColTextInput';
import { SERVICES } from '@shared/constants/apiConfig';
import { LoaderBackdrop } from '@shared/components/LoaderBackdrop';
import { PAY_PLAN_TYPE, PAY_PLAN_TYPE_OPTIONS } from '../constants';
import { requestUpdateUser } from '../requests';

interface IUserMappingData {
    userId: string;
    userName: string;
    paySystemUserId: string;
    payPlanType: string;
}

const userMappingColumns: GridColDef[] = [
        dataGridCol('userName', {
            headerName: 'User'
        }),
        dataGridCol('paySystemUserId', {
            editable: true,
            renderEditCell: (params) => <GridColTextInput params={params} />,
        }),
        dataGridCol('payPlanType', {
            editable: true,
            valueFormatter: (params) => PAY_PLAN_TYPE[params.value as keyof typeof PAY_PLAN_TYPE],
            renderEditCell: (params) => {
                const updatedParams = { ...params, value: PAY_PLAN_TYPE[params.value as keyof typeof PAY_PLAN_TYPE]};
                return <GridColSelect options={PAY_PLAN_TYPE_OPTIONS} params={updatedParams} />;//dropdown options from BE
            },
        }),
    ].map(column => ({ minWidth: 250, flex: 1, ...column }));

export const UserMapping = () => {
    const { data: userMappingData = [], error: userMappingError, mutate: mutateUserData } = useSWR<IUserMappingData[]>(`${SERVICES.USER}/users/plan`); 
    
    const [isLoading, setIsLoading ] = useState(!userMappingData && !userMappingError);

    const gridActions = useMemo(() => ({
        onSave: async(row: IUserMappingData) => {
            setIsLoading(true);
            const userData = {
                paySystemUserId: row.paySystemUserId,
                payPlanType: row.payPlanType.replace(/[ -]/g, '')
            };
            await requestUpdateUser(row.userId, userData);
            await mutateUserData();
            setIsLoading(false);
        },
    }), []);

    const sortedUsers = useMemo<IUserMappingData[]>(
        () => sortBy<IUserMappingData>(userMappingData, 'user'),
        [userMappingData],
    );

    return (
        <PageWrapper>
            <LoaderBackdrop open={isLoading} handleClose={() => setIsLoading(false)} />
            <PhoenixGrid
                withHeader
                canNotCopy
                noButton
                autoHeight
                pagination={false}
                isRowSelectable={() => false}
                columns={userMappingColumns}
                actions={gridActions}
                rows={mapGridIndexes(sortedUsers)}
            />
        </PageWrapper>
    );
};
